import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useUI } from "../UIContext";

const TWIN_LABEL = {
  boiler: "Boiler Twin", cooling_tower: "Cooling Tower Twin", chiller: "Chiller Twin",
  compressor: "Compressor Twin", nitration: "Nitration Twin",
  hydrogenation: "Hydrogenation Twin", distillation: "Distillation Twin", solvent: "Solvent Recovery Twin",
};

const SUGGESTIONS = [
  "Why did profitability drop?",
  "Why is hydrogen consumption rising?",
  "How can I reduce PCF?",
  "What are today's top 3 opportunities?",
  "Why has yield declined?",
  "Where is the plant losing money right now?",
];

export default function AIAdvisor() {
  const { consumeAsk } = useUI();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastTwins, setLastTwins] = useState([]);
  const streamRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.aiNewSession().then(d => alive && setSessionId(d.session_id)).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Auto-fire ?ask from anywhere in the app
  useEffect(() => {
    if (!sessionId) return;
    const ctx = consumeAsk();
    if (ctx?.question) {
      // small delay to ensure session
      setTimeout(() => send(ctx.question), 150);
    }
    // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => {
    if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || !sessionId || busy) return;
    setBusy(true);
    setMessages(m => [...m, { role: "user", content: t }]);
    setInput("");
    try {
      const r = await api.aiChat(sessionId, t);
      setMessages(m => [...m, { role: "assistant", content: r.narrative, twins: r.payload?.twin_references || [] }]);
      setLastTwins(r.payload?.twin_references || []);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "AI Advisor temporarily unavailable. Please retry.", twins: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" data-testid="ai-advisor" style={{ height: "100%" }}>
      <div className="page-title">
        <div>
          <h1>AI Advisor</h1>
          <div className="sub">Causality-aware. Numbers grounded in the live simulation.</div>
        </div>
      </div>

      <div className="ai-shell">
        <div className="ai-stream" ref={streamRef} data-testid="ai-stream">
          {messages.length === 0 && (
            <div className="ai-bubble assistant" data-testid="ai-welcome">
              <strong>Welcome to OIM AI Advisor.</strong>
              {"\n\n"}I synthesize live twin outputs + economic + carbon engines into structured causal explanations. Try one of the suggested questions below, or ask anything about today's plant performance.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`ai-bubble ${m.role}`} data-testid={`ai-msg-${i}`}>
              {m.content}
              {m.role === "assistant" && m.twins?.length > 0 && (
                <div className="ai-twin-chips">
                  {m.twins.map(t => <span key={t} className="chip" style={{ color: "var(--brand-amber)" }}>{TWIN_LABEL[t] || t}</span>)}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="ai-bubble assistant" data-testid="ai-loading" style={{ opacity: 0.65 }}>
              Analyzing twin outputs…
            </div>
          )}
        </div>

        <div>
          <div className="ai-suggestions" data-testid="ai-suggestions">
            {SUGGESTIONS.map(s => (
              <button key={s} className="ai-suggestion" onClick={() => send(s)} data-testid={`suggestion-${s.slice(0,20).replace(/[^a-z0-9]/gi,"-").toLowerCase()}`}>{s}</button>
            ))}
          </div>
          <form className="ai-prompt-bar" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input
              className="ai-input"
              placeholder="Ask anything about plant performance…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
              data-testid="ai-input"
            />
            <button className="btn primary" type="submit" disabled={busy || !input.trim()} data-testid="ai-send">Send</button>
          </form>
        </div>
      </div>
    </div>
  );
}
